import { useState, useEffect, useMemo } from 'react';
import { FooterColumn, FooterMenuItem, FooterSocialLinks } from '../Footer/Footer';
import language from '../utils/LanguageSettings';
import { useAuth } from '../contexts/AuthProvider';

interface WindowFooterData {
    menuColumns?: FooterColumn[];
    bottomMenuLinks?: FooterMenuItem[];
    socialLinks?: FooterSocialLinks;
    copyrightText?: string;
    dpmsText?: string;
    footerTagline?: string;
    language?: 'en' | 'zh' | '_ch';
    customerType?: string;
    showFooter?: boolean;
}

interface UseFooterDataReturn {
    menuColumns: FooterColumn[];
    bottomMenuLinks: FooterMenuItem[];
    socialLinks: FooterSocialLinks;
    copyrightText: string;
    dpmsText: string;
    footerTagline: string;
    language: 'en' | 'zh';
    loading: boolean;
    showFooter: boolean;
}

export const useFooterData = (): UseFooterDataReturn => {
    const { userData, ready } = useAuth();
    const authData = useMemo(() => (userData || {}) as any, [userData]);
    const [footerData, setFooterData] = useState<UseFooterDataReturn>({
        menuColumns: [],
        bottomMenuLinks: [],
        socialLinks: {},
        copyrightText: '',
        dpmsText: '',
        footerTagline: '',
        language: (authData.currentLang === 'zh' ? 'zh' : language),
        loading: true,
        showFooter: false
    });

    useEffect(() => {
        const loadFooterData = () => {
            try {
                const wpData = authData?.footerData;
                
                if (wpData) {
                    // Convert language format from '_ch' to 'zh', keep 'en' as 'en'
                    //const language = normalizeLang(wpData.language || defaultLang);
                    
                    setFooterData({
                        menuColumns: wpData.menuColumns || [],
                        bottomMenuLinks: wpData.bottomMenuLinks || [],
                        socialLinks: wpData.socialLinks || {},
                        copyrightText: wpData.copyrightText || '',
                        dpmsText: wpData.dpmsText || '',
                        footerTagline: wpData.footerTagline || '',
                        language: language,
                        loading: false,
                        showFooter: wpData.showFooter !== false // Default to true if not specified
                    });
                } else {
                    // No footer data available - show default footer layout
                    setFooterData(prev => ({
                        ...prev,
                        language: language,
                        loading: false,
                        showFooter: true
                    }));
                }
            } catch (error) {
                console.error('Error loading footer data:', error);
                setFooterData(prev => ({
                    ...prev,
                    loading: false,
                    showFooter: true
                }));
            }
        };

        if (!ready) {
            return;
        }

        if (authData?.footerData) {
            loadFooterData();
        } else {
            setFooterData(prev => ({
                ...prev,
                loading: false,
                showFooter: true
            }));
        }
    }, [ready, authData]);

    return footerData;
};
